import { liveQuery } from "dexie";
import { useCallback, useEffect, useState } from "react";

import { getGatewayBaseUrl } from "../config";
import { PosPromotionRuntimeGatewayClient } from "../gateway/promotion-runtime-client";
import { readLocalEmployeeSession } from "../local-store/employee-session";
import {
  applyPromotionBootstrapPayload,
  applyPromotionDeltaPayload,
  readPromotionRuntime,
  type LocalPromotionRuntime,
} from "../local-store/promotion-runtime";
import { useRuntimeAutoSync } from "../sync/use-runtime-auto-sync";
import type { TerminalCredentialState } from "../local-store/terminal-state";

export type PromotionRuntimeStatus = "loading" | "local" | "syncing" | "ready" | "error";

const emptyRuntime: LocalPromotionRuntime = { initialized: false, revision: 0, promotions: [] };

export function usePosPromotionRuntime(terminal: TerminalCredentialState | null, online: boolean) {
  const identity = terminal?.identity ?? null;
  const deviceSecret = terminal?.deviceSecret ?? "";
  const scopeKey = identity
    ? `${identity.organization_id}:${identity.branch_id}:${identity.commerce_channel_id}:${identity.node_id}`
    : "";
  const [runtime, setRuntime] = useState<LocalPromotionRuntime>(emptyRuntime);
  const [status, setStatus] = useState<PromotionRuntimeStatus>("loading");
  const [runtimeError, setRuntimeError] = useState<string | null>(null);

  const sync = useCallback(async () => {
    if (!identity || !deviceSecret || !online) return;
    const baseUrl = getGatewayBaseUrl();
    if (!baseUrl) {
      setRuntimeError("ยังไม่ได้กำหนด MORE POS Gateway");
      setStatus("error");
      return;
    }
    const employeeSession = await readLocalEmployeeSession();
    if (!employeeSession?.sessionToken) return;

    setStatus("syncing");
    setRuntimeError(null);
    const client = new PosPromotionRuntimeGatewayClient({ baseUrl });
    let local = await readPromotionRuntime(identity);

    if (!local.initialized) {
      const bootstrap = await client.bootstrap({
        deviceSecret,
        sessionToken: employeeSession.sessionToken,
      });
      if (!bootstrap.ok || !bootstrap.data) {
        setRuntimeError(bootstrap.error ?? "POS_PROMOTION_RUNTIME_LOAD_FAILED");
        setStatus(local.initialized ? "local" : "error");
        return;
      }
      await applyPromotionBootstrapPayload(identity, bootstrap.data);
      local = await readPromotionRuntime(identity);
    }

    let revision = local.revision;
    for (let page = 0; page < 20; page += 1) {
      const delta = await client.delta({
        deviceSecret,
        sessionToken: employeeSession.sessionToken,
        sinceRevision: revision,
        limit: 500,
      });
      if (!delta.ok || !delta.data) {
        const latest = await readPromotionRuntime(identity);
        setRuntime(latest);
        setRuntimeError(delta.error ?? "POS_PROMOTION_RUNTIME_LOAD_FAILED");
        setStatus(latest.initialized ? "local" : "error");
        return;
      }
      revision = await applyPromotionDeltaPayload(identity, delta.data);
      if (!delta.data.has_more) break;
      if (page === 19) {
        setRuntimeError("PROMOTION_RUNTIME_PAGE_GUARD_REACHED");
        setStatus("error");
        return;
      }
    }

    const latest = await readPromotionRuntime(identity);
    setRuntime(latest);
    setRuntimeError(null);
    setStatus("ready");
  // identity object may be rehydrated whenever the cart changes. scopeKey is the
  // stable dependency so cart interaction never retriggers network sync.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceSecret, online, scopeKey]);

  useEffect(() => {
    if (!identity || !scopeKey) {
      setRuntime(emptyRuntime);
      setStatus("loading");
      return;
    }
    const subscription = liveQuery(() => readPromotionRuntime(identity)).subscribe({
      next: (value) => {
        setRuntime(value);
        setStatus((current) => current === "syncing" ? current : value.initialized ? "local" : "loading");
      },
    });
    return () => subscription.unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKey]);

  useRuntimeAutoSync({
    enabled: Boolean(scopeKey && online),
    sync,
    triggerKey: scopeKey,
  });

  return { runtime, status, runtimeError, sync };
}
